import { Avatar, Box, Flex, Image, Text, Badge, Divider, FormControl, FormLabel, Input, Grid, Button, Tooltip, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, Textarea, ModalFooter } from '@chakra-ui/react'
import BackgroundProfile from '../../../../assets/images/CardBackgroundImg.jpg'
import DummyPerson from "../../../../assets/images/DummyPerson.svg"
import CopyIcon from '../../../../assets/images/CopyIcon.svg'
import DepartmentFrame from "../../../../assets/images/DepartmentFrame.svg"
import BranchFrame from '../../../../assets/images/BranchFrame.svg'
import EmailFrame from '../../../../assets/images/EmailFrame.svg'
import PhoneFrame from '../../../../assets/images/PhoneFrame.svg'
import MyDiv from './leaveEmployeeCard.style'
import { useEffect, useState } from 'react';
import { AlertCard, CustomToast } from '../../../../components';
import { ILeaveRequest } from '../../LeaveRequest/modal';
import { baseUrl, useFetch } from '../../../../hooks/useFetch';
import { useAuth } from '../../../../hooks/useAuth'
import { showDate } from '../../../../utilities/helper'


type LeaveEmployeeCardProps = {
  leaveData: ILeaveRequest | null;
  onClose: () => void;
  onReload: () => void;
}

// short leave times come back as "HH:mm:ss" (TimeOnly) - trim seconds for display
const formatTime = (t?: string | null) => {
  if (!t) return '';
  return t.length >= 5 ? t.slice(0, 5) : t;
};

const LeaveEmployeeCard = ({ leaveData, onClose, onReload }: LeaveEmployeeCardProps) => {
  const { addToast } = CustomToast();
  const { fetchApi } = useFetch(addToast);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertStatus, setAlertStatus] = useState<"Approved" | "Rejected" | null>(null);
  const { loginObj } = useAuth();
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!leaveData?.employeeId) return;

    const fetchAvatar = async () => {
      try {
        const res = await fetch(`${baseUrl}Hrlense_Employee/profilepic/${leaveData?.employeeId}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${loginObj?.token || ""}`,
          },
        });

        if (!res.ok) throw new Error("Avatar not found");

        const blob = await res.blob();
        if (blob.size > 0) {
          setAvatarUrl(URL.createObjectURL(blob));
        } else {
          setAvatarUrl(null);
        }
      } catch (err) {
        console.error("Error fetching avatar", err);
        setAvatarUrl(null);
      }
    };

    fetchAvatar();
  }, [leaveData?.employeeId]);

  const handleActionClick = (status: "Approved" | "Rejected") => {
    if (status === "Rejected") {
      setRejectModalOpen(true);
      setAlertStatus("Rejected");
    } else {
      setAlertStatus("Approved");
      setAlertOpen(true);
    }
  };


  const handleConfirmStatusChange = async () => {
    setLoading(true);
    if (leaveData && alertStatus) {
      let payload: any = {
        id: leaveData.id,
        status: alertStatus === "Approved" ? true : false,
      };

      if (alertStatus === "Rejected") {
        payload.rejectReason = rejectReason;
      }
      const response = await fetchApi("Hrlense_Leave/leaveApproval", "PUT", payload, "", "");
      if (response) {
        setRejectReason("");
        onReload();
        onClose();
      }
    }
    setAlertOpen(false);
    setLoading(false);
  };


  const copyToClipboard = (text: string, type: string) => {
    navigator.clipboard.writeText(text);
    addToast({ message: `${type} copied!`, status: 'success' });
  }

  const isShortLeave = !!leaveData?.isShortLeave;

  return (
    <MyDiv>
      <Box className="page_wrapper">
        <Box position="relative" h="140px">
          <Image src={BackgroundProfile} alt="Cover" w="100%" h="100%" objectFit="cover" />
        </Box>
        <Flex align="center" gap={4} px={5} mt={-10}>
          <Avatar src={avatarUrl ? avatarUrl : DummyPerson} size="xl" name="" />
          <Box w="100%" mt={12}>
            <Flex justifyContent="space-between" alignItems="center" width="100%">
              <Tooltip label={leaveData?.name} placement="top" hasArrow>
                <Text className="font-poppins text_xl text_medium font_dark" maxWidth="14vw" isTruncated>{leaveData?.name}</Text>
              </Tooltip>
              <Text className="font-poppins text_sm text_medium" maxWidth="9vw" isTruncated color={leaveData?.empStatus === "Active" ? "green.500" : "red.500"}>{leaveData?.empStatus}</Text>
            </Flex>

            <Flex gap={3} mt={2} alignItems="center" flexWrap="wrap">
              <Badge className="badgeId font-poppins text_medium">{leaveData?.empId}
                <Image src={CopyIcon} alt="Copy" cursor="pointer" ml={2} onClick={(e) => {
                  e.stopPropagation();
                  copyToClipboard(leaveData?.empId ?? "", "Employee ID");
                }}
                />
              </Badge>

              <Tooltip label={leaveData?.designationName} placement="top" hasArrow>
                <Badge className="badgeDegi font-poppins text_sm text_medium" maxWidth="13vw" isTruncated>{leaveData?.designationName}</Badge>
              </Tooltip>

              {isShortLeave && (
                <Badge colorScheme="purple" className="font-poppins text_sm text_medium">
                  Short Leave
                </Badge>
              )}
            </Flex>
          </Box>
        </Flex>
        <Divider orientation="horizontal" mt={2} />
        <Box px={4} mt={4}>
          <Text className="font-poppins font_dark text_medium text_sm" mb="8px">Basic Information</Text>
          <Flex className="info_grid">
            <Flex align="center" gap={2}>
              <Image src={DepartmentFrame} />
              <Flex flexDirection="column">
                <Text className="font-poppins text_medium" fontSize="8px" color="rgba(0, 0, 0, 0.5)">Department</Text>
                <Text className="font-poppins font_dark text_medium text_sm">{leaveData?.departmentName}</Text>
              </Flex>
            </Flex>
            <Flex align="center" gap={2}>
              <Image src={BranchFrame} />
              <Flex flexDirection="column">
                <Text className="font-poppins text_medium" fontSize="8px" color="rgba(0, 0, 0, 0.5)">Branch</Text>
                <Text className="font-poppins font_dark text_medium text_sm">{leaveData?.branchName}</Text>
              </Flex>
            </Flex>
            <Flex align="center" gap={2}>
              <Image src={EmailFrame} />
              <Flex flexDirection="column">
                <Text className="font-poppins  text_medium" fontSize="8px" color="rgba(0, 0, 0, 0.5)">Email</Text>
                <Tooltip label={leaveData?.email} placement="top" hasArrow>
                  <Text className="font-poppins font_dark text_medium text_sm" maxWidth="8vw" isTruncated>{leaveData?.email}</Text>
                </Tooltip>
              </Flex>
            </Flex>
            <Flex align="center" gap={2}>
              <Image src={PhoneFrame} />
              <Flex flexDirection="column">
                <Text className="font-poppins  text_medium" fontSize="8px" color="rgba(0, 0, 0, 0.5)">Phone</Text>
                <Text className="font-poppins font_dark text_medium text_sm">{leaveData?.phone}</Text>
              </Flex>
            </Flex>
          </Flex>
        </Box>
        <Divider orientation="horizontal" mt={2} />
        <Box px={4} mt={4} mb={3}>
          <Text className='font-poppins text_lg text_medium' fontWeight="bold" fontSize="md" mb={4}>
            Leave Application
          </Text>

          {isShortLeave ? (
            <>
              <Grid display="flex" gap={3} mt={2}>
                <FormControl>
                  <FormLabel className="font-poppins text_sm text_medium" color="rgba(0, 0, 0, 0.5)">Leave Date</FormLabel>
                  <Input className="font-poppins text_sm text_medium" value={showDate(leaveData?.fromDate)} readOnly />
                </FormControl>
              </Grid>
              <Grid display="flex" gap={3} mt={2}>
                <FormControl>
                  <FormLabel className="font-poppins text_sm text_medium" color="rgba(0, 0, 0, 0.5)">Start Time</FormLabel>
                  <Input className="font-poppins text_sm text_medium" value={formatTime(leaveData?.startTime)} readOnly />
                </FormControl>
                <FormControl>
                  <FormLabel className="font-poppins text_sm text_medium" color="rgba(0, 0, 0, 0.5)">End Time</FormLabel>
                  <Input className="font-poppins text_sm text_medium" value={formatTime(leaveData?.endTime)} readOnly />
                </FormControl>
              </Grid>
            </>
          ) : (
            <>
              <Grid display="flex" gap={3} mt={2}>
                <FormControl >
                  <FormLabel className="font-poppins text_sm text_medium" color="rgba(0, 0, 0, 0.5)">Leave From</FormLabel>
                  <Input className="font-poppins text_sm text_medium" value={showDate(leaveData?.fromDate)} readOnly />
                </FormControl>
                <FormControl >
                  <FormLabel className="font-poppins text_sm text_medium" color="rgba(0, 0, 0, 0.5)">Session</FormLabel>
                  <Input className="font-poppins text_sm text_medium" value={leaveData?.startSession || ''} readOnly />
                </FormControl>
              </Grid>
              <Grid display="flex" gap={3} mt={2}>
                <FormControl >
                  <FormLabel className="font-poppins text_sm text_medium" color="rgba(0, 0, 0, 0.5)">Leave To</FormLabel>
                  <Input className="font-poppins text_sm text_medium" value={showDate(leaveData?.toDate)} readOnly />
                </FormControl>
                <FormControl >
                  <FormLabel className="font-poppins text_sm text_medium" color="rgba(0, 0, 0, 0.5)">Session</FormLabel>
                  <Input className="font-poppins text_sm text_medium" value={leaveData?.endSession || ''} readOnly />
                </FormControl>
              </Grid>
            </>
          )}

          <FormControl mt={2}>
            <FormLabel className="font-poppins text_sm text_medium" color="rgba(0, 0, 0, 0.5)">Reason</FormLabel>
            <Box className="font-poppins text_sm text_medium" p={3} border="1px solid #ccc" borderRadius="md" whiteSpace="normal" overflowY="auto" minH="150px" dangerouslySetInnerHTML={{ __html: leaveData?.reason ?? "" }} />
          </FormControl>

          <FormControl mt={2}>
            <FormLabel className="font-poppins text_sm text_medium">Work Delegation</FormLabel>
            <Input className="font-poppins text_sm text_medium" value={leaveData?.workDelegation || ''} readOnly />
          </FormControl>
          <Box py={7} display="flex" justifyContent="end" gap={4}>
            {leaveData?.status === "Pending" ? (
              <>
                <Button color='red' className="font-poppins text_sm text_medium" variant='ghost' isDisabled={!leaveData.canApprove} onClick={() => handleActionClick("Rejected")} >
                  Reject
                </Button>
                <Button colorScheme='green' className="font-poppins text_sm text_medium" isDisabled={!leaveData.canApprove} onClick={() => handleActionClick("Approved")} >
                  Approve
                </Button>
              </>
            ) : (
              <Text className='font-poppins text_medium text_xsm' color={leaveData?.status === "Approved" ? "green.500" : "red.500"} >
                {leaveData?.status === "Approved" && `Approved ${leaveData?.actionBy}`}
                {leaveData?.status === "Rejected" && `Rejected by ${leaveData?.actionBy}`}
              </Text>
            )}
          </Box>
        </Box>

      </Box >


      <Modal isOpen={rejectModalOpen} onClose={() => setRejectModalOpen(false)} isCentered size="lg" >
        <ModalOverlay />
        <ModalContent borderRadius="12px">
          <ModalHeader className="font-poppins text_medium">Reject Reason</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <Box height="150px">
              <Textarea placeholder="Enter reject reason..." value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} className="font-poppins text_regular" height="120px" />
            </Box>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" onClick={() => setRejectModalOpen(false)}>
              Cancel
            </Button>

            <Button bg="#006838" color="white" ml={3} onClick={() => {
              if (!rejectReason.trim()) {
                addToast({ message: "Please enter reject reason!", status: "warning" });
                return;
              }
              setRejectModalOpen(false);
              setAlertOpen(true);
            }}
            >
              Submit
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>


      <AlertCard isOpen={alertOpen} onClose={() => setAlertOpen(false)} isLoading={loading} onConfirm={handleConfirmStatusChange} title="Approve Leave"
        description={`Are you sure you want to ${alertStatus?.toLowerCase()} leave request?`}
        confirmLabel="Confirm"
        colorScheme={alertStatus === "Approved" ? "green" : "red"}
      />
    </MyDiv >
  )
}

export default LeaveEmployeeCard